import React, { useState } from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonalInjuryIcon from '@mui/icons-material/PersonalInjury';
import { PermanentInjury, WarbandMemberDTO } from '../../types';
import { SelectPermanentInjuryModal } from '../modals/SelectPermanentInjuryModal';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { editButtonOffsetSx } from '../../App';

interface MemberPermanentInjuriesProps {
  injuries: PermanentInjury[];
  editMode?: boolean;
  memberRole?: 'wizard' | 'apprentice' | 'soldier';
  member?: WarbandMemberDTO;
  onUpdateInjuries?: (injuryIds: string[]) => void;
}

export const MemberPermanentInjuries: React.FC<MemberPermanentInjuriesProps> = ({
  injuries,
  editMode = false,
  memberRole,
  member,
  onUpdateInjuries,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [injuryPendingDelete, setInjuryPendingDelete] = useState<{ injury: PermanentInjury; idx: number } | null>(null);
  const canEdit = editMode && memberRole !== 'soldier' && !!onUpdateInjuries;

  if (!canEdit && injuries.length === 0) return null;

  return (
    <Stack direction="column" spacing={1} alignItems="flex-start" sx={{ mb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Permanent Injuries
        </Typography>
        {canEdit && (
          <IconButton size="small" sx={editButtonOffsetSx} onClick={() => setIsAddModalOpen(true)}>
            <AddIcon />
          </IconButton>
        )}
      </Stack>

    <Stack direction="column" spacing={1} alignItems="flex-start" sx={{ mb: 4 }}>
        {injuries.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            None
          </Typography>
        ) : (
          injuries.map((injury, idx) => {
            const statParts: string[] = [];
            if (injury.fightChange != null) statParts.push(`Fight ${injury.fightChange > 0 ? '+' : ''}${injury.fightChange}`);
            if (injury.shootChange != null) statParts.push(`Shoot ${injury.shootChange > 0 ? '+' : ''}${injury.shootChange}`);
            if (injury.moveChange != null) statParts.push(`Move ${injury.moveChange > 0 ? '+' : ''}${injury.moveChange}`);
            if (injury.willChange != null) statParts.push(`Will ${injury.willChange > 0 ? '+' : ''}${injury.willChange}`);
            if (injury.healthChange != null) statParts.push(`Health ${injury.healthChange > 0 ? '+' : ''}${injury.healthChange}`);
            if (injury.castingNumberChange != null) statParts.push(`Casting ${injury.castingNumberChange > 0 ? '+' : ''}${injury.castingNumberChange}`);
            const effectLabel = statParts.length > 0 ? ` (${statParts.join(', ')})` : '';
            return (
              <Tooltip key={`${injury.id}-${idx}`} title={injury.description} arrow>
                <Chip
                  size="small"
                  icon={<PersonalInjuryIcon />}
                  label={`${injury.name}${effectLabel}`}
                  color="error"
                  variant="outlined"
                  onDelete={canEdit ? () => setInjuryPendingDelete({ injury, idx }) : undefined}
                />
              </Tooltip>
            );
          })
        )}
    </Stack>

      {canEdit && (
        <SelectPermanentInjuryModal
          open={isAddModalOpen}
          member={member ?? null}
          onClose={() => setIsAddModalOpen(false)}
          onSelect={(injury) => {
            onUpdateInjuries!([...injuries.map((i) => i.id), injury.id]);
            setIsAddModalOpen(false);
          }}
        />
      )}

      <ConfirmActionDialog
        open={injuryPendingDelete !== null}
        title="Remove permanent injury"
        message={injuryPendingDelete ? `Remove "${injuryPendingDelete.injury.name}"?` : ''}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setInjuryPendingDelete(null)}
        onConfirm={() => {
          if (!injuryPendingDelete) return;
          onUpdateInjuries!(injuries.filter((_, i) => i !== injuryPendingDelete.idx).map((inj) => inj.id));
          setInjuryPendingDelete(null);
        }}
      />
    </Stack>
  );
};
